/* global DetectUserAgent */
import { useEffect, useRef } from "react";
import { isNil } from "ramda";
import PropTypes from "prop-types";
import { useInterval, usePrevious } from "react-use";

import ThemePresenter from "presenters/ThemePresenter";
import { useNotifications, useJoinModal, usePlayerControls, useVideoTracker } from "hooks";
import { jjLogger } from "utils/logUtils";
import Placeholder from "components/Placeholder";
import PlayerContainer from "components/PlayerContainer";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";

import { Root, Video } from "./styles";

const HTMLPlayer = ({
  src,
  poster,
  autoplay = false,
  controls = true,
  loop = false,
  muted = false,
  joinable,
  isChangeVideo = false,
  className,
  aspectRatio,
  onError,
  onLoad,
  inviteTheme,
  bordered,
  withPlayerContainer = false,
  templateGroup,
}) => {
  const { addErrorNotification } = useNotifications();
  const { joinModalVisible, openJoinModal, JOIN_MODAL_TIMEOUT } = useJoinModal();
  const prevJoinModalVisible = usePrevious(joinModalVisible);
  const player = useRef(null);
  const loaded = !isNil(src);
  const { init, pauseVideo, setVolume } = usePlayerControls(player);
  const { handleResetsOnPlay, resetProgressTracking, handleTimeUpdateTrackers } = useVideoTracker(templateGroup);

  useEffect(() => {
    init();
  }, [player]);

  const pausePlayer = () => {
    if (!player.current) return;

    player.current.pause();
  };

  const resetPlayer = () => {
    if (!player.current) return;

    player.current.pause();
    player.current.currentTime = 0;
    player.current.load();
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (player.current.webkitExitFullscreen) {
      player.current.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const handleError = (event) => {
    const { code, message } = event.target.error;
    const msg = `Uh oh! We’re having some trouble playing this video right now. Please refresh the page, or try again later.`;
    addErrorNotification(msg);
    if (onError) onError();
    jjLogger.log(`Video Player Error.
      code: ${code}
      message: ${message}
      src: ${src}`);
  };

  const videoDownloaded = () => {
    window.prerenderReady = true;
  };

  const renderPlaceholder = () => {
    return <Placeholder variant="rect" ratio={aspectRatio} animation="wave" />;
  };

  const disableFullScreen = () => {
    return DetectUserAgent.isFB4Android() ? "nofullscreen" : "";
  };

  const renderVideoPlayer = () => {
    return isChangeVideo ? (
      renderPlaceholder()
    ) : (
      <Video
        data-testid="htmlPlayerVideo"
        ref={player}
        src={src}
        playsInline
        disablePictureInPicture
        controlsList={`nodownload noplaybackrate ${disableFullScreen()}`}
        poster={poster}
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
        controls={controls}
        className={className}
        onLoadedData={() => {
          if (onLoad) onLoad();
        }}
        onError={handleError}
        onContextMenu={(e) => e.preventDefault()}
        onVolumeChange={() => setVolume()}
        onPlay={handleResetsOnPlay}
        onEnded={() => {
          if (!loop) resetPlayer();
          resetProgressTracking();
        }}
        onTimeUpdate={handleTimeUpdateTrackers}
        onCanPlayThrough={videoDownloaded}
      />
    );
  };

  useInterval(
    () => {
      if (joinable && player.current.currentTime > JOIN_MODAL_TIMEOUT) {
        openJoinModal();
        exitFullscreen();
      }
    },
    joinModalVisible ? null : 1000,
  );

  useEffect(() => {
    if (prevJoinModalVisible && !joinModalVisible) {
      resetPlayer();
    }
  }, [joinModalVisible]);

  useEffect(() => {
    resetPlayer();
  }, [src, poster]);

  useEffect(() => {
    if (pauseVideo) {
      pausePlayer();
    }
  }, [pauseVideo]);

  const content = () => (
    <Root data-testid="htmlPlayer" aspectRatio={aspectRatio}>
      {loaded ? renderVideoPlayer() : renderPlaceholder()}
    </Root>
  );

  jjLogger.logDebug("HTMLPlayer.js: Rendering");
  jjLogger.logDebug("HTMLPlayer.js: src");
  jjLogger.logDebug(src);
  jjLogger.logDebug("HTMLPlayer.js: poster");
  jjLogger.logDebug(poster);
  jjLogger.logDebug("HTMLPlayer.js: autoplay");
  jjLogger.logDebug(autoplay);
  jjLogger.logDebug("HTMLPlayer.js: controls");
  jjLogger.logDebug(controls);
  jjLogger.logDebug("HTMLPlayer.js: loop");
  jjLogger.logDebug(loop);
  jjLogger.logDebug("HTMLPlayer.js: muted");
  jjLogger.logDebug(muted);
  jjLogger.logDebug("HTMLPlayer.js: joinable");
  jjLogger.logDebug(joinable);

  return withPlayerContainer ? (
    <PlayerContainer inviteTheme={inviteTheme} bordered={bordered}>
      {content()}
    </PlayerContainer>
  ) : (
    content()
  );
};

HTMLPlayer.propTypes = {
  src: PropTypes.string,
  poster: PropTypes.string,
  autoplay: PropTypes.bool,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  joinable: PropTypes.bool.isRequired,
  isChangeVideo: PropTypes.bool,
  className: PropTypes.string,
  aspectRatio: PropTypes.string,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  bordered: PropTypes.bool,
  withPlayerContainer: PropTypes.bool,
  inviteTheme: ThemePresenter.shape(),
  templateGroup: TemplateGroupPresenter.shape(),
};

export default HTMLPlayer;
